import {
  GoalFilterDocumentKeys,
  GoalGroupBy,
  GoalSortingMode,
  GoalType,
  GroupByMode,
  OrderDirection,
  PageEvents,
} from '@griply/types';
import {
  getProjectsOverviewOrder,
  projectOnlyTypes,
  shouldShowPremiumBanner,
} from '@griply/utils';
import {
  useCreateUnlimitedGoalsAllowed,
  useGoalOverviewReorder,
  useSortedGoals,
} from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import {
  useProjectsOverviewFilterDocument,
  useUpdateProjectsOverviewFilterDocument,
  useUpdateUserSettings,
  useUserHadTrial,
} from 'features/user';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PremiumBanner } from 'shared/components/ui/premium-banner';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useTaskCounts } from 'shared/contexts/task-counts/use-task-counts';
import { useEntityLinks } from 'shared/hooks/use-entity-links';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useUser } from 'shared/hooks/use-user';
import { ID } from 'shared/types/id';
import { GoalFilterList, SortBar } from 'shared/views/goal-filter-list';

import { EmptyBanner } from '../empty-banner';
import * as Styled from './projects-list.style';

const reorderableGroupings = new Set<GoalGroupBy>([
  GroupByMode.Goal,
  GroupByMode.LifeArea,
]);

export const ProjectsList: React.FC = () => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  useTrackPageVisitEvent(PageEvents.GoalsList, undefined);

  const user = useUser();
  const openPremiumDialog = useOpenPremiumDialog();
  const userHadTrial = useUserHadTrial();
  const {
    allowed: createProjectAllowed,
    enforced: projectLimitEnforced,
    limit: projectLimit,
    total: totalProjects,
  } = useCreateUnlimitedGoalsAllowed(GoalType.Project);
  const showPremiumBanner = shouldShowPremiumBanner({
    allowed: createProjectAllowed,
    enforced: projectLimitEnforced,
    limit: projectLimit,
    total: totalProjects,
  });
  const sortedGoals = useSortedGoals();
  const filterDocument = useProjectsOverviewFilterDocument();
  const updateFilterDocument = useUpdateProjectsOverviewFilterDocument();
  const updateUserSettings = useUpdateUserSettings();

  const showCompletedProjects =
    user?.settings?.projectsOverview?.roadmap?.showCompletedProjects ?? true;

  const groupBy = filterDocument.groupByMode ?? GroupByMode.Goal;
  const sortingMode = filterDocument.sortingMode ?? undefined;
  const sortDirection =
    user?.settings?.projectSortDirection ?? OrderDirection.Asc;

  const onGroupingReorder = useGoalOverviewReorder(
    groupBy,
    GoalFilterDocumentKeys.ProjectsOverview,
  );
  // None/Deadline emit their own section shapes (quarter keys, root); only these two persist.
  const onReorder = reorderableGroupings.has(groupBy)
    ? onGroupingReorder
    : undefined;
  const links = useEntityLinks();
  const lifeAreas = useLocalizedLifeAreas();
  const taskCounts = useTaskCounts();
  const goalRoadmapSorting = user?.settings?.goalRoadmapSorting;
  const goalGroupSorting = user?.settings?.goalGroupSorting;

  const { customOrder, goalChildrenByParentId } = useMemo(
    () =>
      getProjectsOverviewOrder({
        goals: sortedGoals,
        lifeAreas,
        links,
        groupBy,
        sortMode: sortingMode,
        sortDirection,
        goalRoadmapSorting,
        goalGroupSorting,
        taskCounts: taskCounts?.byGoalId,
        goalProgressCounts: taskCounts?.byGoalProgress,
      }),
    [
      sortedGoals,
      lifeAreas,
      links,
      groupBy,
      sortingMode,
      sortDirection,
      goalRoadmapSorting,
      goalGroupSorting,
      taskCounts?.byGoalId,
      taskCounts?.byGoalProgress,
    ],
  );

  const hasProjects = useMemo(
    () => sortedGoals.some((goal) => goal.type === GoalType.Project),
    [sortedGoals],
  );

  const onSortChange = useCallback(
    (mode: GoalSortingMode) => {
      updateFilterDocument({ sortingMode: mode });
      updateUserSettings({ projectSortDirection: OrderDirection.Asc });
    },
    [updateFilterDocument, updateUserSettings],
  );

  const onSortDirectionToggle = useCallback(() => {
    updateUserSettings({
      projectSortDirection:
        sortDirection === OrderDirection.Asc
          ? OrderDirection.Desc
          : OrderDirection.Asc,
    });
  }, [sortDirection, updateUserSettings]);

  const onSectionCollapse = (collapsedSections: ID[]) =>
    updateFilterDocument({ collapsedSections });

  if (!hasProjects) {
    return <EmptyBanner />;
  }

  return (
    <Styled.Container ref={scrollRef}>
      <Styled.GroupingContainer>
        <SortBar
          value={sortingMode}
          direction={sortDirection}
          onChange={onSortChange}
          onDirectionToggle={onSortDirectionToggle}
          hideLifeArea
        />
        <GoalFilterList
          filterDocument={filterDocument}
          goalTypeFilter={projectOnlyTypes}
          customOrder={customOrder}
          goalChildrenByParentId={goalChildrenByParentId}
          nestGoals
          onReorder={onReorder}
          onSectionCollapse={onSectionCollapse}
          hideArchived={false}
          hideCompleted={!showCompletedProjects}
          hideLifeArea
          showProjectParent
          scrollElement={scrollRef}
        />
        {showPremiumBanner && (
          <PremiumBanner
            title={t(
              projectLimitEnforced
                ? 'projects.premium-banner.title'
                : 'projects.premium-banner.not-enforced.title',
              { total: totalProjects, limit: projectLimit },
            )}
            description={t(
              projectLimitEnforced
                ? userHadTrial
                  ? 'projects.premium-banner.description'
                  : 'pages.projects.premium-banner.try-for-free.description'
                : 'projects.premium-banner.not-enforced.description',
              { limit: projectLimit },
            )}
            onClick={openPremiumDialog}
            isTrial={!userHadTrial}
          />
        )}
      </Styled.GroupingContainer>
    </Styled.Container>
  );
};
